import { useState } from 'react';

import { TimeLogGrid } from '@/components/timelogs/TimeLogGrid';
import { Segmented } from '@/components/ui/segmented';
import { Stat, StatRow } from '@/components/ui/stat';
import { hours, monthLabel } from '@/lib/format';
import { GRID_AXES, type GridAxis } from '@/lib/timelogs';

import { SAMPLE_LOGS, SAMPLE_MONTH, SAMPLE_PROJECTS } from './sample-time-logs';

/**
 * LP に出す工数明細の見本。
 *
 * **文章や作り物の図ではなく、管理画面で使っている部品そのものを出す。**
 * 「どんな実績が出るのか」への答えは製品の画面そのものなので、
 * `TimeLogGrid` と `Stat` を工数明細 (`routes/TimeLogsPage.tsx`) と同じ形で使う。
 *
 * **見る軸の切り替えも実物と同じ `Segmented` で動かせるようにする。**
 * 「人単位でもプロジェクト単位でも見られる」は工数明細の中心の機能で、
 * 静止画で説明すると 2 枚の画像が要るうえ実物と食い違う。同じ記録が
 * その場で束ね直されることを、触って確かめられる形で見せる。
 * 選択肢は画面と同じ `GRID_AXES` を読む (語を 2 箇所に持たない)。
 *
 * LP は未ログインの公開ページなので、ログイン後の文脈は `readOnly` で切る
 * (見出しはリンクにならず、マスを押しても訂正フォームは開かない)。
 * 軸の切り替えは表示だけを変える操作で API を呼ばないので、`readOnly` の
 * 下でもそのまま動く。
 * データは `sample-time-logs.ts` の固定値で、実データではない。
 * 数値タイルは画面と同じくその記録から計算する (ここに数字を書かない)。
 */
export function SampleTimeLogs() {
  // 既定は画面 (管理画面フルのロール) と同じ「人単位」
  const [axis, setAxis] = useState<GridAxis>('member');

  const total = SAMPLE_LOGS.reduce((sum, log) => sum + log.hours, 0);
  const capitalized = SAMPLE_LOGS.reduce(
    (sum, log) => sum + (log.isCapitalizedLog ? log.hours : 0),
    0,
  );

  return (
    <div className="flex min-w-0 flex-col border border-line">
      <div className="flex items-center justify-between gap-4 border-b border-line bg-surface-raised px-4 py-2.5">
        <span className="font-mono text-xs tracking-wider text-ink-faint">
          {`工数明細 / ${monthLabel(SAMPLE_MONTH)}`}
        </span>
        <span className="font-mono text-xs tracking-wider text-ink-faint">SAMPLE</span>
      </div>

      <div className="flex min-w-0 flex-col gap-4 bg-surface p-3 sm:gap-6 sm:p-6">
        {/* 管理画面は 3 列だが、LP は狭い画面でも読めないといけない。
            数値タイルだけは縦に積み、仕切りを左から上に移す。 */}
        <StatRow className="grid-cols-1 sm:grid-cols-3 [&>*+*]:border-t sm:[&>*+*]:border-t-0">
          <Stat label="合計" value={hours(total)} />
          <Stat label="資産化" value={hours(capitalized)} tone="brand" />
          <Stat label="費用化" value={hours(total - capitalized)} />
        </StatRow>

        {/* 画面と同じく、グリッドは枠の中で横スクロールする (31 日分の列がある) */}
        <div className="min-w-0 border border-line bg-surface-raised">
          {/* 画面では絞り込みと並ぶ行。LP に検索は出さないので軸だけを置く。
              狭い幅では説明が折り返して切り替えを押し下げるため、縦に積む。 */}
          <div className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4">
            <span className="text-xs leading-relaxed text-ink-subtle">
              {'同じ記録を、人からもプロジェクトからも見られます。'}
            </span>
            <Segmented
              value={axis}
              onChange={setAxis}
              options={GRID_AXES}
              label="見る軸"
              className="self-start sm:self-auto"
            />
          </div>
          <TimeLogGrid
            month={SAMPLE_MONTH}
            logs={SAMPLE_LOGS}
            projects={SAMPLE_PROJECTS}
            axis={axis}
            canCreate={() => false}
            onChanged={() => {}}
            readOnly
          />
        </div>
      </div>
    </div>
  );
}
